/**
 * Step 4: Generate
 * Narrated progress screen with staged messages.
 * Uses SelfServeHeader + ShadCN Card for step list.
 * Polls mediaPlanApi.getGenerationStatus() every 3s for live progress.
 * Auto-transitions to review on completion.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import SelfServeHeader from '@/components/mediaplan/SelfServeHeader';
import ErrorCard from '@/components/mediaplan/ErrorCard';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { CheckCircle2, Circle, Loader2, Mail } from 'lucide-react';
import mediaPlanApi from '@/services/mediaPlanApi';
import useMediaPlanFlow from '@/hooks/useMediaPlanFlow';
import { toast } from 'sonner';

const POLL_INTERVAL = 3000;
const EMAIL_PROMPT_DELAY = 12000;

export default function GenerateProgress() {
  const navigate = useNavigate();
  const { planId } = useParams();
  const { campaignId } = useMediaPlanFlow();

  // The effective ID to poll with (campaignId from flow context takes priority)
  const effectiveId = campaignId || planId;

  // ── State ──────────────────────────────────────────────────────────────────
  const [currentStep, setCurrentStep] = useState(0);
  const [totalSteps, setTotalSteps] = useState(0);
  const [progress, setProgress] = useState(0);
  const [completed, setCompleted] = useState(false);
  const [failed, setFailed] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showEmailOption, setShowEmailOption] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  // Accumulated step data — we build the list as the API reports each step
  const [steps, setSteps] = useState([]);

  const pollRef = useRef(null);
  const emailTimeoutRef = useRef(null);

  // ── Polling logic ──────────────────────────────────────────────────────────

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  const poll = useCallback(async () => {
    try {
      const res = await mediaPlanApi.getGenerationStatus(effectiveId);

      // Map API response to local state
      setTotalSteps(res.totalSteps);
      setProgress(res.percentComplete);

      // Accumulate step info as it becomes available (1-indexed from API)
      setSteps((prev) => {
        const updated = [...prev];
        // Ensure we have entries up to the current step
        for (let i = updated.length; i < res.currentStep; i++) {
          // For past steps we don't have labels for, use placeholders
          updated.push({ label: `Step ${i + 1}`, detail: '' });
        }
        // Update the current step with the latest label/detail from the API
        if (res.currentStep > 0 && res.currentStep <= res.totalSteps) {
          const idx = res.currentStep - 1;
          if (idx < updated.length) {
            updated[idx] = { label: res.stepLabel, detail: res.stepDetail };
          }
        }
        return updated;
      });

      // currentStep in local state is 0-indexed for display consistency
      // API currentStep is 1-indexed; when completed it equals totalSteps
      if (res.status === 'completed') {
        setCurrentStep(res.totalSteps);
        setProgress(100);
        setCompleted(true);
        stopPolling();
        setTimeout(() => {
          navigate(`/campaign/${effectiveId}/review`);
        }, 2000);
      } else if (res.status === 'failed') {
        setFailed(true);
        setErrorMessage(res.stepDetail || 'Generation failed unexpectedly.');
        stopPolling();
      } else {
        // generating — currentStep is 1-indexed, convert to 0-indexed for display
        setCurrentStep(res.currentStep - 1);
      }
    } catch (err) {
      console.error('[GenerateProgress] Polling error:', err);
      setFailed(true);
      setErrorMessage(err.message || 'Could not reach the server. Please try again.');
      stopPolling();
    }
  }, [effectiveId, navigate, stopPolling]);

  const startPolling = useCallback(() => {
    stopPolling();
    // Immediate first poll
    poll();
    pollRef.current = setInterval(poll, POLL_INTERVAL);
  }, [poll, stopPolling]);

  // ── Effects ────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!effectiveId) return;

    startPolling();

    emailTimeoutRef.current = setTimeout(() => {
      setShowEmailOption(true);
    }, EMAIL_PROMPT_DELAY);

    return () => {
      stopPolling();
      if (emailTimeoutRef.current) {
        clearTimeout(emailTimeoutRef.current);
      }
    };
  }, [effectiveId, startPolling, stopPolling]);

  // ── Retry handler ──────────────────────────────────────────────────────────

  const handleRetry = () => {
    setFailed(false);
    setErrorMessage('');
    setCompleted(false);
    setProgress(0);
    setCurrentStep(0);
    setSteps([]);
    mediaPlanApi.resetMockGeneration(effectiveId);
    startPolling();
  };

  // ── Email notification handler ─────────────────────────────────────────────

  const handleEmailNotify = async () => {
    try {
      // TODO: get email from auth context when available
      const email = 'user@example.com';
      await mediaPlanApi.requestEmailNotification(effectiveId, email);
      setEmailSent(true);
      toast.success('We\'ll email you when your ads are ready.');
    } catch (err) {
      toast.error('Could not register for email notification. Please try again.');
    }
  };

  // ── No campaign/plan ID guard ──────────────────────────────────────────────

  if (!effectiveId) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <SelfServeHeader showNav={false} showAuth={false} />
        <div className="flex-1 flex items-center justify-center px-6">
          <ErrorCard
            title="No campaign found"
            message="We couldn't find a campaign to generate. Please start a new campaign."
            fallbackPath="/campaign/new"
            fallbackLabel="Start new campaign"
          />
        </div>
      </div>
    );
  }

  // ── Error state ────────────────────────────────────────────────────────────

  if (failed) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <SelfServeHeader showNav={false} showAuth={false} />
        <div className="flex-1 flex items-center justify-center px-6">
          <ErrorCard
            title="Generation failed"
            message={errorMessage}
            onRetry={handleRetry}
            fallbackPath="/campaign/new"
            fallbackLabel="Start over"
          />
        </div>
      </div>
    );
  }

  // ── Build display steps ────────────────────────────────────────────────────
  // If the API has reported totalSteps but we haven't accumulated that many yet,
  // pad with placeholder entries so the skeleton is visible.
  const displaySteps = [...steps];
  while (displaySteps.length < totalSteps) {
    displaySteps.push({ label: `Step ${displaySteps.length + 1}`, detail: '' });
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SelfServeHeader showNav={false} showAuth={false} />

      {/* Progress bar beneath header */}
      <Progress
        value={progress}
        className="h-1 rounded-none [&>[data-slot=progress-indicator]]:bg-gradient-to-r [&>[data-slot=progress-indicator]]:from-coral [&>[data-slot=progress-indicator]]:to-teal"
      />

      {/* Main */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          {/* Animated ring */}
          <div className="flex justify-center mb-10">
            <div className="relative">
              <svg className="w-28 h-28" viewBox="0 0 120 120">
                <circle cx="60" cy="60" r="54" fill="none" stroke="hsl(var(--border))" strokeWidth="4" />
                <circle
                  cx="60" cy="60" r="54" fill="none"
                  stroke="url(#progressGradient)" strokeWidth="4" strokeLinecap="round"
                  strokeDasharray={`${2 * Math.PI * 54}`}
                  strokeDashoffset={`${2 * Math.PI * 54 * (1 - progress / 100)}`}
                  className="transition-all duration-300 ease-out"
                  transform="rotate(-90 60 60)"
                />
                <defs>
                  <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#FF6B6B" />
                    <stop offset="100%" stopColor="#4ECDC4" />
                  </linearGradient>
                </defs>
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                {completed ? (
                  <CheckCircle2 className="h-10 w-10 text-teal" />
                ) : (
                  <span className="text-2xl font-bold tracking-tight">{Math.round(progress)}%</span>
                )}
              </div>
            </div>
          </div>

          {/* Status text */}
          <div className="text-center mb-10">
            <h1 className="text-xl font-bold mb-1.5">
              {completed ? 'Your ads are ready!' : 'Creating your ads...'}
            </h1>
            <p className="text-sm text-muted-foreground">
              {completed ? 'Redirecting to review...' : (
                displaySteps[currentStep]?.label
                  ? `${displaySteps[currentStep].label}...`
                  : 'Starting generation...'
              )}
            </p>
          </div>

          {/* Steps */}
          <div className="space-y-2">
            {displaySteps.map((step, i) => {
              const isActive = i === currentStep && !completed;
              const isDone = i < currentStep || completed;
              const isPending = i > currentStep && !completed;

              return (
                <Card
                  key={i}
                  className={cn(
                    'transition-all duration-300',
                    isActive && 'border-coral/30 shadow-sm',
                    isDone && 'opacity-60',
                    isPending && 'opacity-30'
                  )}
                >
                  <CardContent className="flex items-start gap-3 py-3 px-4">
                    <div className="mt-0.5 shrink-0">
                      {isDone ? (
                        <CheckCircle2 className="h-5 w-5 text-teal" />
                      ) : isActive ? (
                        <Loader2 className="h-5 w-5 text-coral animate-spin" />
                      ) : (
                        <Circle className="h-5 w-5 text-muted-foreground/40" />
                      )}
                    </div>
                    <div>
                      <div className={cn(
                        'text-sm font-medium',
                        isActive && 'text-foreground',
                        (isDone || isPending) && 'text-muted-foreground'
                      )}>
                        {step.label}
                      </div>
                      <div className="text-xs text-muted-foreground">{step.detail}</div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Email option */}
          {showEmailOption && !completed && !emailSent && (
            <Card className="mt-8 animate-[fadeInUp_0.3s_ease]">
              <CardContent className="text-center py-4">
                <p className="text-sm text-muted-foreground mb-3">
                  Taking longer than expected? We'll email you when it's ready.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  onClick={handleEmailNotify}
                >
                  <Mail className="h-3.5 w-3.5" />
                  Email me when ready
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Email sent confirmation */}
          {emailSent && !completed && (
            <Card className="mt-8">
              <CardContent className="text-center py-4">
                <p className="text-sm text-muted-foreground">
                  We'll send you an email when your ads are ready.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
